import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { ShareRow } from "@/components/site/Share";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shareUrlFor = (slug) => (slug ? `${BACKEND}/api/share/${slug}` : undefined);
/*
  Reusable editorial category section.
  props:
    id, kicker (english eyebrow), title (hindi), accent (title highlight word optional),
    items [{id,kicker,title,img,time,lead}], variant: 'leadList' | 'cards',
    tint: boolean (alternating background)
*/
export default function CategoryBlock({
  id,
  kicker,
  title,
  accent,
  items = [],
  variant = "leadList",
  tint = false,
  moreLabel = "और ख़बरें",
}) {
  const lead = items.find((i) => i.lead) || items[0];
  const rest = items.filter((i) => i.id !== lead?.id);

  return (
    <section
      id={id}
      data-testid={`section-${id}`}
      className={`${tint ? "bg-[#EFEEE8]" : "bg-[#F4F4F0]"} border-b border-[#0F0F0F]/15`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-14 md:py-20">
        <div className="flex items-end justify-between mb-8 md:mb-12 flex-wrap gap-4">
          <div>
            <div className="eyebrow text-[#D92525] mb-3">{kicker}</div>
            <h2 className="font-serif-dev font-black text-[34px] md:text-[50px] leading-[1.02]">
              {accent ? (
                <>
                  {title} <span className="italic text-[#D92525]">{accent}</span>
                </>
              ) : (
                title
              )}
            </h2>
          </div>
          <a
            href={`/category/${id}`}
            data-testid={`${id}-more`}
            className="font-sans-dev text-[12px] tracking-[0.24em] uppercase link-underline inline-flex items-center gap-2"
          >
            {moreLabel} <ArrowUpRight size={14} />
          </a>
        </div>

        {variant === "cards" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((n, i) => (
              <motion.a
                key={n.id}
                href={n.slug ? `/news/${n.slug}` : "#"}
                data-testid={`${id}-card-${n.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.07 }}
                className="group block"
              >
                <div className="thumb-wrap aspect-[4/3] mb-4 border border-[#0F0F0F]/15">
                  <img src={n.img} alt={n.title} className="w-full h-full object-contain" />
                </div>
                <div className="eyebrow text-[#D92525] mb-2">{n.kicker} · {n.time}</div>
                <h3 className="font-serif-dev font-bold text-[18px] md:text-[19px] leading-snug headline-link">
                  {n.title}
                </h3>
                <div className="mt-3 pt-3 border-t border-[#0F0F0F]/10">
                  <ShareRow title={n.title} url={shareUrlFor(n.slug)} testid={`share-${id}-${n.id}`} />
                </div>
              </motion.a>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            {/* Lead */}
            <motion.a
              href={lead?.slug ? `/news/${lead.slug}` : "#"}
              data-testid={`${id}-lead-${lead?.id}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="col-span-12 lg:col-span-7 group block"
            >
              <div className="thumb-wrap aspect-[16/10] mb-5 border border-[#0F0F0F]/15">
                <img src={lead?.img} alt={lead?.title} className="w-full h-full object-contain" />
              </div>
              <div className="eyebrow text-[#D92525] mb-3">{lead?.kicker} · {lead?.time}</div>
              <h3 className="font-serif-dev font-black text-[26px] md:text-[34px] leading-[1.1] headline-link max-w-[620px]">
                {lead?.title}
              </h3>
              <div className="mt-4">
                <ShareRow title={lead?.title} url={shareUrlFor(lead?.slug)} testid={`share-${id}-${lead?.id}`} />
              </div>
            </motion.a>

            {/* list */}
            <div className="col-span-12 lg:col-span-5">
              <ul className="divide-y divide-[#0F0F0F]/15">
                {rest.map((m, i) => (
                  <motion.li
                    key={m.id}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.5, delay: i * 0.08 }}
                    className="py-5 first:pt-0"
                  >
                    <a href={m.slug ? `/news/${m.slug}` : "#"} data-testid={`${id}-item-${m.id}`} className="grid grid-cols-12 gap-4 items-start group">
                      <div className="col-span-4 thumb-wrap aspect-[4/3] border border-[#0F0F0F]/15">
                        <img src={m.img} alt={m.title} className="w-full h-full object-contain" />
                      </div>
                      <div className="col-span-8">
                        <div className="eyebrow text-[#D92525] mb-1.5">{m.kicker} · {m.time}</div>
                        <h4 className="font-serif-dev font-bold text-[16px] md:text-[18px] leading-snug headline-link">
                          {m.title}
                        </h4>
                        <div className="mt-2">
                          <ShareRow title={m.title} url={shareUrlFor(m.slug)} testid={`share-${id}-${m.id}`} />
                        </div>
                      </div>
                    </a>
                  </motion.li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
